import { spreadDynamicsScenarios } from '../data';

export default function SpreadDynamicsSection() {
  return (
    <section className="relative w-full bg-background-50 overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-background-300/40 to-transparent" />

      <div className="w-full max-w-main mx-auto px-4 md:px-6 py-12 md:py-18">
        {/* Header */}
        <div className="mb-10 md:mb-14">
          <h2 className="text-[28px] md:text-[38px] font-[750] leading-[1.15] text-foreground-950 tracking-[-0.015em] max-w-[720px]">
            Spreads aren&rsquo;t static — and that changes the comparison
          </h2>
          <p className="mt-4 text-[15px] text-foreground-500 leading-relaxed max-w-[620px]">
            The spread-vs-commission comparison assumes a fixed 1.2 pip spread. In reality, spreads breathe with liquidity — and each pricing model absorbs the movement differently.
          </p>
        </div>

        {/* ── MOBILE: stacked cards ── */}
        <div className="flex flex-col gap-3 lg:hidden">
          {spreadDynamicsScenarios.map((s, i) => (
            <div key={i} className="border border-background-200/70 bg-background-50 overflow-hidden">
              {/* Card header — market name */}
              <div className="px-4 py-3 bg-background-100/60 border-b border-background-200/70">
                <h3 className="text-[14px] font-[700] text-foreground-900 leading-tight">{s.market}</h3>
                <p className="text-[11px] text-foreground-400 mt-0.5">{s.liquidity}</p>
              </div>

              {/* 3-column cost grid */}
              <div className="grid grid-cols-3 divide-x divide-background-200/70 px-0 py-4">
                {/* Spread-Only */}
                <div className="px-3 text-center">
                  <div className="text-[9px] font-bold uppercase tracking-[0.08em] text-foreground-400 mb-2">
                    Spread
                  </div>
                  <div className="text-[20px] font-[750] text-foreground-800 tabular-nums leading-none">
                    ${s.spreadOnly.costPerLot.toFixed(2)}
                  </div>
                  <div className="text-[10px] text-foreground-400 mt-1.5 leading-tight">
                    {s.spreadOnly.spreadPips} pips<br />no comm.
                  </div>
                </div>

                {/* ECN */}
                <div className="px-3 text-center">
                  <div className="text-[9px] font-bold uppercase tracking-[0.08em] text-foreground-400 mb-2">
                    ECN
                  </div>
                  <div className="text-[20px] font-[750] text-foreground-800 tabular-nums leading-none">
                    ${s.ecn.total.toFixed(2)}
                  </div>
                  <div className="text-[10px] text-foreground-400 mt-1.5 leading-tight">
                    {s.ecn.spreadPips} pips<br />${s.ecn.commission.toFixed(2)} comm
                  </div>
                </div>

                {/* Verdict */}
                <div className="px-3 text-center">
                  <div className="text-[9px] font-bold uppercase tracking-[0.08em] text-foreground-400 mb-2">
                    Verdict
                  </div>
                  <div className="text-[13px] font-[700] text-accent-600 leading-snug">
                    {s.advantage}
                  </div>
                </div>
              </div>

              {/* Note */}
              <div className="px-4 pb-4">
                <p className="text-[12px] text-foreground-500 leading-relaxed border-t border-background-200/60 pt-3">
                  {s.note}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* ── DESKTOP: ledger table ── */}
        <div className="hidden lg:block border border-background-200/70 bg-background-50">
          {spreadDynamicsScenarios.map((s, i) => (
            <div key={i} className={i > 0 ? 'border-t border-background-200/70' : ''}>
              <div className="px-10 py-7">
                {/* Top: market name + three cost columns */}
                <div className="flex items-baseline gap-0">
                  {/* Market label */}
                  <div className="w-[280px] flex-shrink-0">
                    <h3 className="text-[15px] font-[700] text-foreground-900">{s.market}</h3>
                    <p className="text-[12px] text-foreground-500 mt-0.5">{s.liquidity}</p>
                  </div>

                  {/* Cost columns */}
                  <div className="flex items-baseline flex-1">
                    {/* Spread-Only */}
                    <div className="flex-1">
                      <div className="text-[10px] font-bold uppercase tracking-[0.10em] text-foreground-400 mb-1">
                        Spread-Only
                      </div>
                      <div className="text-[26px] font-[750] text-foreground-800 tabular-nums leading-none">
                        ${s.spreadOnly.costPerLot.toFixed(2)}
                      </div>
                      <div className="text-[12px] text-foreground-400 mt-0.5">
                        {s.spreadOnly.spreadPips} pips &middot; no commission
                      </div>
                    </div>

                    {/* ECN */}
                    <div className="flex-1">
                      <div className="text-[10px] font-bold uppercase tracking-[0.10em] text-foreground-400 mb-1">
                        ECN
                      </div>
                      <div className="text-[26px] font-[750] text-foreground-800 tabular-nums leading-none">
                        ${s.ecn.total.toFixed(2)}
                      </div>
                      <div className="text-[12px] text-foreground-400 mt-0.5">
                        {s.ecn.spreadPips} pips + ${s.ecn.commission.toFixed(2)} comm
                      </div>
                    </div>

                    {/* Verdict */}
                    <div className="flex-1">
                      <div className="text-[10px] font-bold uppercase tracking-[0.10em] text-foreground-400 mb-1">
                        Verdict
                      </div>
                      <div className="text-[15px] font-[700] text-accent-600 tabular-nums">
                        {s.advantage}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bottom: context note */}
                <p className="mt-4 text-[13px] text-foreground-500 leading-relaxed pl-[280px]">
                  {s.note}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Takeaway */}
        <p className="mt-8 text-[14px] text-foreground-500 leading-relaxed max-w-[680px]">
          Don&rsquo;t compare spread vs commission at a single point in time. The real question is:{' '}
          <span className="text-foreground-800 font-semibold">
            how does each model behave across the full range of conditions you actually trade in?
          </span>
          {' '}If you only trade calm London hours, the ECN edge is reliable. If you trade around news, the math shifts — sometimes dramatically.
        </p>
      </div>
    </section>
  );
}